import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useEmbeddedEffect } from '@machinemetrics/mm-react-embeddable';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

import {
  actionCloseLaborTicketModal,
  actionSetEmbeddedLaborTicket,
} from 'lib/actions';
import { getCurrentLaborTicket } from 'lib/selectors/getLaborTickets';
import { getLaborTicketAction } from 'lib/selectors/getLaborTicketAction';
import { getLaborTicketModalOpen } from 'lib/selectors/getLaborTicketModalOpen';
import { getIsAuthorizationModalOpen } from 'lib/selectors/getIsAuthorizationModalOpen';
import { LABOR_TICKET_HOST } from 'lib/appConfig';
import { getEmbeddedLaborTicket } from 'lib/selectors/getEmbeddedLaborTicket';
import { useRequestRecentActivitySets } from 'lib/hooks';
import { getMachine } from 'lib/selectors/getMachine';
import { getEnableOperatorVerification } from 'lib/selectors/getEnableOperatorVerification';
import { getLaborScopeTotalParts } from 'lib/selectors/getScopeTotalParts';
import { getIsApmERPJobFlow } from 'lib/selectors/getIsApmERPJobFlow';
import { getFAICurrentInspection } from 'lib/selectors/getFAICurrentInspection';
import { getFAIEnabled } from 'lib/selectors/getFAIEnabled';
import { FAI_STATUS } from 'lib/constants';
import useHandleLaborTicketSubmit from '../LaborTicket/hooks/useHandleLaborTicketSubmit';
import MMEmbeddableZoneWithLoader from '../MMEmbeddableZoneWithLoader';
import MMDrawer from '../MMDrawer';

const EmbeddedLaborTicket = ({
  id,
  path,
  personId,
  splitTime,
  newWorkOrderOperationRef,
}) => {
  const dispatch = useDispatch();
  const { refetch: refetchRecentActivitySets } = useRequestRecentActivitySets();
  const laborTicketAction = useSelector(getLaborTicketAction);
  const machine = useSelector(getMachine);
  const enableOperatorVerification = useSelector(getEnableOperatorVerification);
  const laborScopeTotalParts = useSelector(getLaborScopeTotalParts);
  const laborTicketModalOpen = useSelector(getLaborTicketModalOpen);
  const isAPMControlled = useSelector(getIsApmERPJobFlow);

  const faiEnabled = useSelector(getFAIEnabled);
  const currentInspection = useSelector(getFAICurrentInspection);

  // false if current inspection is pending or in progress
  const blockCompleteSetup = useMemo(() => {
    return (
      faiEnabled &&
      (currentInspection?.status === FAI_STATUS.PENDING ||
        currentInspection?.status === FAI_STATUS.IN_PROGRESS)
    );
  }, [currentInspection, faiEnabled]);

  // Create close modal callback for the submit handler
  const closeModal = useCallback(() => {
    dispatch(actionCloseLaborTicketModal());
    dispatch(actionSetEmbeddedLaborTicket(null));
  }, [dispatch]);

  const handleSubmit = useHandleLaborTicketSubmit(closeModal);

  useEmbeddedEffect(id, 'labor-ticket-form:close', () => {
    dispatch(actionCloseLaborTicketModal());
    // Refetch activity sets to ensure state is in sync after embedded form closes
    refetchRecentActivitySets();
  });

  // No longer refresh on not-found; the embedded app will close itself

  useEmbeddedEffect(id, 'labor-ticket-form:submit', handleSubmit);

  return (
    <MMEmbeddableZoneWithLoader
      id={id}
      src={`${LABOR_TICKET_HOST}`}
      path={path}
      context={{
        personId,
        action: laborTicketAction,
        enableOperatorVerification,
        opened: laborTicketModalOpen,
        openedAt: splitTime || undefined,
        laborScopeTotalPartsByMachine: machine?.machineRef
          ? { [machine.machineRef]: laborScopeTotalParts }
          : {},
        isAPMControlled,
        newWorkOrderOperationRef: newWorkOrderOperationRef || undefined,
        allowCompleteSetup: !blockCompleteSetup,
      }}
      params={{
        language: i18n.language,
      }}
    />
  );
};

EmbeddedLaborTicket.propTypes = {
  id: PropTypes.string.isRequired,
  path: PropTypes.string,
  personId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  splitTime: PropTypes.string,
  newWorkOrderOperationRef: PropTypes.number,
};

const LaborTicketWrapper = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const id = 'labor-ticket-form';
  const embeddedLaborTicket = useSelector(getEmbeddedLaborTicket);
  const currentLaborTicket = useSelector(getCurrentLaborTicket);
  const laborTicketFormOpen = useSelector(getLaborTicketModalOpen);
  const authorizationModalOpen = useSelector(getIsAuthorizationModalOpen);

  const liveLaborTicket = embeddedLaborTicket || currentLaborTicket;

  // Pin the labor ticket when modal opens to prevent race condition
  // where subscription clears the ticket before the embedded form response arrives
  const [pinnedLaborTicket, setPinnedLaborTicket] = useState(null);

  useEffect(() => {
    if (laborTicketFormOpen && liveLaborTicket && !pinnedLaborTicket) {
      // Pin the ticket when modal opens
      setPinnedLaborTicket(liveLaborTicket);
    } else if (!laborTicketFormOpen && pinnedLaborTicket) {
      // Unpin when modal closes
      setPinnedLaborTicket(null);
    }
  }, [laborTicketFormOpen, liveLaborTicket, pinnedLaborTicket]);

  // Use pinned ticket while modal is open, fall back to live ticket
  const laborTicket = pinnedLaborTicket || liveLaborTicket;
  const laborTicketRef = laborTicket?.laborTicketRef;
  const splitQs = laborTicket?.splitTime
    ? `?splitTime=${encodeURIComponent(laborTicket.splitTime)}`
    : '';
  const path = laborTicketRef
    ? `/labor-ticket/${laborTicketRef}${splitQs}`
    : null;

  useEffect(() => {
    if (authorizationModalOpen) {
      dispatch(actionCloseLaborTicketModal());
    }
  }, [authorizationModalOpen, dispatch]);

  if (!laborTicket || !laborTicketRef) {
    return null;
  }

  return (
    <MMDrawer
      isOpen={laborTicketFormOpen}
      onClose={() => {
        dispatch(actionCloseLaborTicketModal());
      }}
      title={t('Confirm work produced')}
    >
      <EmbeddedLaborTicket
        id={id}
        path={path}
        personId={laborTicket.personId}
        splitTime={laborTicket.splitTime || undefined}
        newWorkOrderOperationRef={
          laborTicket.newWorkOrderOperationRef || undefined
        }
      />
    </MMDrawer>
  );
};

export default LaborTicketWrapper;
